import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Compass, ArrowLeft, Home, Construction } from 'lucide-react';

/**
 * NotFound — catch-all for broken / missing / not-yet-built routes.
 * Renders inside the authenticated <Layout/>, so sidebar + topbar stay intact.
 */
export default function NotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location?.pathname || '';

  // Heuristic — paths that look like they belong to an existing module group
  // are shown as "feature under development" instead of a hard 404.
  const knownModuleGroups = [
    '/finance', '/contracts', '/closeout', '/reports',
    '/estimation', '/material-gaps', '/risks',
  ];
  const isFutureFeature = knownModuleGroups.some((m) => path.startsWith(m));

  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center" data-testid="not-found-page">
      <div className="w-20 h-20 rounded-full bg-indigo-50 flex items-center justify-center mb-5">
        {isFutureFeature ? (
          <Construction size={36} className="text-[#3b4872]" />
        ) : (
          <Compass size={36} className="text-[#3b4872]" />
        )}
      </div>

      <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">
        {isFutureFeature ? 'Coming Soon' : 'Page Not Found'}
      </div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight mb-3">
        {isFutureFeature ? 'This feature is under development' : "We can't find that page"}
      </h1>
      <p className="text-sm text-slate-600 max-w-md mb-1">
        {isFutureFeature
          ? "Our team is actively building this module. You'll see it light up here shortly."
          : "The link you followed may be broken, or the page may have been moved."}
      </p>
      <div className="text-xs text-slate-400 font-mono mt-2 mb-8" data-testid="not-found-path">{path}</div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2.5 border border-slate-300 hover:border-[#3b4872] hover:text-[#3b4872] rounded-sm text-sm font-semibold"
          data-testid="not-found-back-btn"
        >
          <ArrowLeft size={14} /> Go Back
        </button>
        <Link
          to="/app"
          className="flex items-center gap-2 px-4 py-2.5 bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm text-sm font-semibold"
          data-testid="not-found-home-btn"
        >
          <Home size={14} /> Take me Home
        </Link>
      </div>

      <p className="text-[11px] text-slate-400 mt-10">
        Think this is a bug? Reach out to your admin or IT team.
      </p>
    </div>
  );
}
